import {useState} from "react";
import {Alert, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {useGetGscSitemapsQuery} from "~/reduxs/api/gscApiSlice";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

const fmtNum = (n) => Number(n || 0).toLocaleString('vi-VN');

/** 'YYYY-MM-DD HH:MM:SS' (giờ máy chủ) → 'dd/mm/yyyy HH:MM'. Parse tay để không lệch múi giờ. */
function fmtDate(s) {
	if (!s) return '—';
	const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(s));
	return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(s);
}

/** Badge trạng thái xử lý của sitemap (features.md §9): lỗi > pending > cảnh báo > OK. */
function statusTag(r) {
	if (r.errors > 0) return <Tag color="error">Có lỗi</Tag>;
	if (r.is_pending) return <Tag>Đang xử lý</Tag>;
	if (r.warnings > 0) return <Tag color="warning">Có cảnh báo</Tag>;
	return <Tag color="success">Thành công</Tag>;
}

/**
 * Tab "Sitemap" (v1 CHỈ-ĐỌC): danh sách sitemap đã submit của property, lấy từ Sitemaps
 * API và cache ở DB. Chưa có submit/xoá (cần scope ghi + Owner — xem features.md §11).
 * Nút "Đồng bộ" ép BE gọi lại Google (bỏ qua TTL 6h).
 */
function GscSitemaps({projectId}) {

	const [syncTick, setSyncTick] = useState(0);

	const {data = {rows: [], synced_at: null, stale: false}, isFetching} =
		useGetGscSitemapsQuery(syncTick ? {projectId, refresh: syncTick} : {projectId});

	const rows = data.rows || [];

	const columns = [
		{
			title: 'Sitemap', dataIndex: 'path', key: 'path', ellipsis: true,
			render: (v, r) => (
				<span>
					{r.is_sitemaps_index && (
						<Tooltip title="File sitemap index (chứa nhiều sitemap con)">
							<Tag color="blue" style={{marginRight: 6}}>Index</Tag>
						</Tooltip>
					)}
					<a href={v} target="_blank" rel="noreferrer">{v}</a>
				</span>
			),
		},
		{title: 'Loại', dataIndex: 'type', key: 'type', width: 110, render: (v) => v || '—'},
		{title: 'Trạng thái', key: 'status', width: 130, render: (_, r) => statusTag(r)},
		{
			title: (
				<Tooltip title="Số URL Google ĐỌC ĐƯỢC từ sitemap — KHÔNG phải số trang đã index. Xem số index thực ở báo cáo Lập chỉ mục.">
					URL đã gửi <FontAwesomeIcon icon="fa-light fa-circle-info" />
				</Tooltip>
			),
			dataIndex: 'submitted', key: 'submitted', width: 130, align: 'right',
			sorter: (a, b) => a.submitted - b.submitted,
			render: (v) => fmtNum(v),
		},
		{
			title: 'Cảnh báo / Lỗi', key: 'issues', width: 130, align: 'center',
			render: (_, r) => (
				<span>
					<span style={{color: r.warnings > 0 ? '#d97706' : '#9ca3af'}}>{fmtNum(r.warnings)}</span>
					<span style={{color: '#d1d5db'}}> / </span>
					<span style={{color: r.errors > 0 ? '#dc2626' : '#9ca3af'}}>{fmtNum(r.errors)}</span>
				</span>
			),
		},
		// Cột phụ (ngày giờ) — ẩn dưới 768px cho gọn màn nhỏ.
		{title: 'Ngày submit', dataIndex: 'last_submitted_at', key: 'last_submitted_at', width: 150, responsive: ['md'], render: fmtDate},
		{title: 'Google đọc cuối', dataIndex: 'last_downloaded_at', key: 'last_downloaded_at', width: 150, responsive: ['md'], render: fmtDate},
	];

	return (
		<div>
			<div className={cn('rangeBar')}>
				<div className={cn('metaRow')} style={{margin: 0}}>
					<FontAwesomeIcon icon="fa-light fa-sitemap" />
					<span>Danh sách sitemap đã submit trong Search Console (chỉ xem).</span>
					{data.synced_at && <span>Đồng bộ lúc {fmtDate(data.synced_at)}</span>}
				</div>
				<Button white small loading={isFetching} onClick={() => setSyncTick(Date.now())}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-arrows-rotate" />}>Đồng bộ</Button>
			</div>

			{data.stale && (
				<Alert type="warning" showIcon style={{marginBottom: 12}}
					message="Không cập nhật được từ Google, đang hiển thị dữ liệu đã lưu."
					description={data.message || undefined} />
			)}

			<Table
				rowKey="path"
				size="small"
				loading={isFetching}
				columns={columns}
				dataSource={rows}
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}}
				locale={{emptyText: 'Chưa có sitemap nào được submit cho property này.'}}
			/>

			<div className={cn('metaRow')} style={{marginTop: 10, color: '#9ca3af', fontSize: 12}}>
				<FontAwesomeIcon icon="fa-light fa-lock" />
				<span>Submit / xoá sitemap sẽ có ở phiên bản sau (cần cấp thêm quyền ghi cho Google).</span>
			</div>
		</div>
	);
}

export default GscSitemaps;
